import { handleResponse } from './common';

const API_URL = process.env.GATSBY_API_URL;

export const uploadSessionsService = {
  createSession,
  getSession,
  addPendingPhotos,
  createPresignedUpload,
  completePresignedUpload,
  uploadPhoto,
  downloadPhoto,
  deletePhoto,
  logPhotoDownloadError,
  downloadPhotoAsDataUrl,
};

async function createSession(payload = {}) {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  };
  return handleResponse(`${API_URL}/upload-sessions`, requestOptions);
}

async function getSession(sessionId) {
  if (!sessionId) {
    throw new Error('sessionId is required');
  }

  const requestOptions = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  };
  return handleResponse(`${API_URL}/upload-sessions/${sessionId}`, requestOptions);
}

async function uploadPhoto(sessionId, file, options = {}) {
  if (!sessionId) {
    throw new Error('sessionId is required');
  }
  if (!file) {
    throw new Error('file is required');
  }

  const usePresigned = shouldUsePresignedUploads(file, options);

  if (usePresigned) {
    try {
      return await uploadPhotoWithPresignedWorkflow(sessionId, file, options);
    } catch (error) {
      if (options.forcePresigned) {
        throw error;
      }
      console.warn('Presigned upload failed, falling back to multipart upload', error);
    }
  }

  const fileName = options.pendingToken
    ? `${options.pendingToken}:::${file.name || file.fileName || 'upload.jpg'}`
    : file.name || file.fileName || 'upload.jpg';

  const endpoint = `${API_URL}/upload-sessions/${sessionId}/photos`;
  const buildFormData = () => {
    const formData = new FormData();
    formData.append('file', file, fileName);
    return formData;
  };

  return uploadWithAutomaticFallback(endpoint, buildFormData);
}

async function addPendingPhotos(sessionId, files = []) {
  if (!sessionId) {
    throw new Error('sessionId is required');
  }
  if (!Array.isArray(files) || files.length === 0) {
    return { pending: [] };
  }

  const body = {
    files: files.map(file => ({
      fileName: file.fileName || file.name,
      size: file.size || null,
      mimeType: file.mimeType || file.type || null,
    })),
  };

  const response = await fetch(`${API_URL}/upload-sessions/${sessionId}/pending`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const message = await safeReadError(response);
    throw new Error(message || 'Failed to queue pending uploads');
  }

  return response.json();
}

async function downloadPhoto(sessionId, photoId, format = 'base64') {
  if (!sessionId || !photoId) {
    throw new Error('sessionId and photoId are required');
  }

  const response = await fetch(
    `${API_URL}/upload-sessions/${sessionId}/photos/${photoId}?format=${format}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    const message =
      (await safeReadError(response)) ||
      `Failed to download photo (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  const data = await response.json();
  if (!data || typeof data !== 'object' || !data.base64) {
    throw new Error('Proxy download returned empty payload');
  }
  return data;
}

async function deletePhoto(sessionId, photoId) {
  if (!sessionId || !photoId) {
    throw new Error('sessionId and photoId are required');
  }

  const response = await fetch(
    `${API_URL}/upload-sessions/${sessionId}/photos/${photoId}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    const message = await safeReadError(response);
    throw new Error(message || 'Failed to delete photo');
  }

  return response.json();
}

async function logPhotoDownloadError(sessionId, photoId, details = {}) {
  if (!sessionId || !photoId) {
    return;
  }
  const payload = {
    ...details,
    timestamp: new Date().toISOString(),
  };
  try {
    await fetch(`${API_URL}/upload-sessions/${sessionId}/photos/${photoId}/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to log photo download error', error);
  }
}

async function downloadPhotoAsDataUrl(sessionId, photoId, fallbackMimeType) {
  const payload = await downloadPhoto(sessionId, photoId);
  if (!payload || typeof payload !== 'object') {
    throw new Error('Download payload missing');
  }
  if (!payload.base64) {
    throw new Error(payload.message || 'Photo download did not include image data');
  }
  const contentType = payload.contentType || fallbackMimeType || 'image/jpeg';
  return {
    dataUrl: `data:${contentType};base64,${payload.base64}`,
    contentType,
  };
}

async function safeReadError(response) {
  try {
    const text = await response.text();
    if (!text) return null;
    try {
      const data = JSON.parse(text);
      if (data && typeof data.message === 'string' && data.message.trim()) {
        return data.message;
      }
    } catch (parseError) {
      // not JSON, fall back to raw text
    }
    return text;
  } catch (error) {
    // ignore body read errors
  }
  return null;
}

async function createPresignedUpload(sessionId, payload = {}) {
  const response = await fetch(
    `${API_URL}/upload-sessions/${sessionId}/photos/presign`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const message = await safeReadError(response);
    throw new Error(message || 'Failed to create presigned upload');
  }

  return response.json();
}

async function completePresignedUpload(sessionId, photoId, payload = {}) {
  const response = await fetch(
    `${API_URL}/upload-sessions/${sessionId}/photos/${photoId}/complete`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const message = await safeReadError(response);
    throw new Error(message || 'Failed to finalize upload');
  }

  return response.json();
}

async function uploadPhotoWithPresignedWorkflow(sessionId, file, options = {}) {
  const displayName = options.originalFileName || file.name || 'upload.jpg';
  const mimeType = file.type || options.originalMimeType || 'application/octet-stream';
  const size = typeof file.size === 'number' ? file.size : options.originalSize || null;

  const presignPayload = {
    fileName: displayName,
    mimeType,
    size,
    pendingId: options.pendingToken || null,
  };

  const presignResponse = await createPresignedUpload(sessionId, presignPayload);
  if (!presignResponse || !presignResponse.upload || !presignResponse.photoId) {
    throw new Error('Unable to initialize upload');
  }

  await uploadFileToS3(presignResponse.upload, file);

  const finalizedFileName = presignResponse.sanitizedFileName || displayName;

  const finalizePayload = {
    fileName: finalizedFileName,
    mimeType,
    size,
    pendingId: options.pendingToken || null,
    sourceKey: presignResponse.sourceKey,
  };

  return completePresignedUpload(
    sessionId,
    presignResponse.photoId,
    finalizePayload
  );
}

async function uploadFileToS3(uploadDescriptor, file) {
  if (!uploadDescriptor || !uploadDescriptor.url) {
    throw new Error('Missing upload target');
  }
  const formData = new FormData();
  const fields = uploadDescriptor.fields || {};
  Object.keys(fields).forEach(key => {
    formData.append(key, fields[key]);
  });
  formData.append('file', file);

  const response = await fetch(uploadDescriptor.url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok && response.status !== 204) {
    const errorText = await safeReadError(response);
    throw new Error(errorText || `S3 upload failed (${response.status})`);
  }
}

function shouldUsePresignedUploads(file, options = {}) {
  return Boolean(options.forcePresigned);
}

async function uploadWithAutomaticFallback(url, buildFormData) {
  const attemptFetchFirst = prefersFetchForUploads();
  if (attemptFetchFirst) {
    try {
      return await uploadPhotoWithFetch(url, buildFormData);
    } catch (error) {
      if (!shouldRetryUploadWithXhr(error)) {
        throw error;
      }
    }
  }

  try {
    return await uploadPhotoWithXhr(url, buildFormData);
  } catch (error) {
    if (!attemptFetchFirst && prefersFetchForUploads()) {
      // On platforms where we tried XHR first, fall back to fetch once.
      return uploadPhotoWithFetch(url, buildFormData);
    }
    throw error;
  }
}

function prefersFetchForUploads() {
  return typeof window === 'undefined' ? true : !isIOSDevice();
}

function shouldRetryUploadWithXhr(error) {
  if (typeof XMLHttpRequest === 'undefined' || !error) {
    return false;
  }
  const message = (error && error.message) || '';
  if (/load failed|network request failed|failed to fetch/i.test(message)) {
    return true;
  }
  return false;
}

async function uploadPhotoWithFetch(url, buildFormData) {
  const payload = typeof buildFormData === 'function' ? buildFormData() : buildFormData;
  const response = await fetch(url, {
    method: 'POST',
    body: payload,
  });

  if (!response.ok) {
    const message = await safeReadError(response);
    throw new Error(message || `Failed to upload photo (${response.status})`);
  }

  return response.json();
}

function uploadPhotoWithXhr(url, buildFormData) {
  return new Promise((resolve, reject) => {
    if (typeof XMLHttpRequest === 'undefined') {
      reject(new Error('XMLHttpRequest is not available'));
      return;
    }

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.withCredentials = false;
      xhr.setRequestHeader('Accept', 'application/json');

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const responseText = xhr.responseText || '{}';
            const parsed = responseText ? JSON.parse(responseText) : {};
            resolve(parsed);
          } catch (parseError) {
            reject(new Error('Failed to parse upload response'));
          }
        } else {
          reject(new Error(readXhrErrorMessage(xhr) || `Failed to upload photo (${xhr.status})`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Network error'));
      };

      const payload = typeof buildFormData === 'function' ? buildFormData() : buildFormData;
      xhr.send(payload);
    } catch (xhrError) {
      reject(xhrError);
    }
  });
}

function readXhrErrorMessage(xhr) {
  if (!xhr) {
    return null;
  }
  const responseText = xhr.responseText;
  if (!responseText) {
    return null;
  }
  try {
    const parsed = JSON.parse(responseText);
    if (parsed && typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message;
    }
  } catch (error) {
    // ignore parse errors, fall back to raw text
  }
  return typeof responseText === 'string' && responseText.trim()
    ? responseText.trim()
    : null;
}

function isIOSDevice() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (platform === 'MacIntel' &&
      typeof navigator.maxTouchPoints === 'number' &&
      navigator.maxTouchPoints > 1)
  );
}
